import { useEffect, useState, useMemo } from "react";
import { IconButton, Button, Select, MenuItem } from "@mui/material";
import VisibilityIcon from "@mui/icons-material/Visibility";
import { useOrders, useUpdateOrder, useAssignOrder } from "api/useOrders.ts";
import { useProducts } from "api/useProducts.ts";
import { useOperators } from "api/useUsers.ts";
import MDBox from "components/MDBox";
import MDBadge from "components/MDBadge";
import EditIcon from "@mui/icons-material/Edit";
import { useTranslation } from "react-i18next";
import { formatSAR, formatDate } from "../../../helper/format";

export default function appData() {
  const { t } = useTranslation();
  const { data: orders, isLoading: loadingOrders } = useOrders();
  const { data: products } = useProducts();
  const { data: operators } = useOperators();
  const updateOrder = useUpdateOrder();
  const assignOrder = useAssignOrder();
  const [selectedProducts, setSelectedProducts] = useState(null);
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [editedOrder, setEditedOrder] = useState(null);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);

  const [notification, setNotification] = useState({
    open: false,
    message: "",
    severity: "success", // "success" | "error"
  });

  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isPaymentVerificationModalOpen, setIsPaymentVerificationModalOpen] = useState(false);
  const [selectedPaymentOrder, setSelectedPaymentOrder] = useState(null);

  const handleViewProducts = (products) => {
    setSelectedProducts(products);
    setIsModalOpen(true);
  };

  const handleVerifyPayment = (order) => {
    setSelectedPaymentOrder(order);
    setIsPaymentVerificationModalOpen(true);
  };

  const handleClosePaymentVerificationModal = () => {
    setIsPaymentVerificationModalOpen(false);
    setSelectedPaymentOrder(null);
  };

  const handleAssignToOperator = async (orderId, userId) => {
    try {
      console.log("Starting order assignment process");
      console.log(`Order ID: ${orderId}, User ID: ${userId}`);

      await assignOrder.mutateAsync({ orderId, userId });

      console.log("Order assigned successfully");
      setNotification({
        open: true,
        message: t("orders.messages.orderAssignedSuccessfully"),
        severity: "success",
      });
    } catch (error) {
      console.error("Error assigning order:", error);
      console.error("Error details:", error.response?.data || error.message);

      setNotification({
        open: true,
        message:
          error.response?.data?.message ||
          error.message ||
          t("orders.messages.errorAssigningOrder"),
        severity: "error",
      });
    }
  };

  const handleAcceptPayment = async () => {
    try {
      const updatedOrder = await updateOrder.mutateAsync({
        id: selectedPaymentOrder._id,
        orderStatus: "Paid",
      });

      setNotification({
        open: true,
        message: t("orders.messages.paymentVerifiedSuccessfully"),
        severity: "success",
      });

      handleClosePaymentVerificationModal();
    } catch (error) {
      console.error("Error verifying payment:", error);
      setNotification({
        open: true,
        message: error.message || t("orders.messages.errorVerifyingPayment"),
        severity: "error",
      });
    }
  };

  const handleRejectPayment = async () => {
    try {
      const updatedOrder = await updateOrder.mutateAsync({
        id: selectedPaymentOrder._id,
        orderStatus: "Cancelled",
      });

      setNotification({
        open: true,
        message: t("orders.messages.paymentRejectedAndOrderCancelled"),
        severity: "success",
      });

      handleClosePaymentVerificationModal();
    } catch (error) {
      console.error("Error rejecting payment:", error);
      setNotification({
        open: true,
        message: error.message || t("orders.messages.errorRejectingPayment"),
        severity: "error",
      });
    }
  };

  const handleCloseModal = () => {
    setSelectedProducts(null);
    setIsModalOpen(false);
  };

  const getSelectedProducts = () => {
    return new Promise((resolve) => {
      const filteredProducts = products
        .filter((product) => selectedProducts?.some((p) => p.product._id === product._id))
        .map((product) => {
          const selectedProduct = selectedProducts.find((p) => p.product._id === product._id);
          return {
            ...product,
            quantity: selectedProduct?.quantity ?? product.quantity,
          };
        });

      resolve(filteredProducts);
    });
  };

  const handleEditClick = (order) => {
    setSelectedOrder(order);
    setEditedOrder({
      orderStatus: order.orderStatus,
    });
    setIsEditModalOpen(true);
  };

  const handleCloseEditModal = () => {
    setIsEditModalOpen(false);
    setSelectedOrder(null);
    setEditedOrder(null);
  };

  const handleSaveChanges = async () => {
    try {
      // Must mirror the backend enum (order.model.ts / order.validation.ts).
      // "InProgress" and "Delivered" were missing, so statuses the UI itself
      // offered — and the one the delivery-agent app writes — were rejected.
      const validStatuses = [
        "Pending",
        "Paid",
        "InProgress",
        "Cancelled",
        "Completed",
        "Delivered",
      ];
      if (!validStatuses.includes(editedOrder.orderStatus)) {
        throw new Error(`Invalid order status: ${editedOrder.orderStatus}`);
      }

      const updatedOrder = await updateOrder.mutateAsync({
        id: selectedOrder._id,
        orderStatus: editedOrder.orderStatus,
      });

      setEditedOrder(updatedOrder);
      setNotification({
        open: true,
        message: t("orders.orderUpdated"),
        severity: "success",
      });

      handleCloseEditModal();
    } catch (error) {
      console.error("Error updating order:", error);
      setNotification({
        open: true,
        message: error.message || t("orders.orderUpdateFailed"),
        severity: "error",
      });
    }
  };

  const rows = useMemo(() => {
    if (loadingOrders) {
      return [
        {
          orderId: t("orders.loading"),
          userId: t("orders.loading"),
          customer: t("orders.loading"),
          totalPrice: t("orders.loading"),
          orderDate: t("orders.loading"),
          paymentMethod: t("orders.loading"),
          orderStatus: (
            <MDBox ml={-1}>
              <MDBadge
                badgeContent={t("orders.loading")}
                color="light"
                variant="gradient"
                size="sm"
              />
            </MDBox>
          ),
          mosque: t("orders.loading"),
          products: t("orders.loading"),
          actions: t("orders.loading"),
          assignTo: t("orders.loading"),
        },
      ];
    }

    return orders?.map((order) => ({
      orderId: order._id,
      userId: order.customer._id,
      customer: order.customer?.name || t("orders.unknownCustomer"),
      totalPrice: formatSAR(order.totalAmount),
      orderDate: formatDate(order.orderDate),
      paymentMethod: (
        <div className="flex flex-col items-center">
          <div>{order.paymentMethod}</div>
          {order.paymentMethod === "bank" &&
            order.orderStatus === "Pending" &&
            order.paymentImage && (
              <Button
                variant="contained"
                color="success"
                size="small"
                onClick={() => handleVerifyPayment(order)}
                sx={{ marginTop: "0.5rem", fontSize: "0.7rem", padding: "2px 8px" }}
              >
                {t("orders.verify")}
              </Button>
            )}
        </div>
      ),
      orderStatus: (
        <MDBadge
          badgeContent={order.orderStatus}
          color={
            order.orderStatus === "Pending"
              ? "warning"
              : order.orderStatus === "Paid"
              ? "success"
              : order.orderStatus === "Cancelled"
              ? "error"
              : order.orderStatus === "Completed"
              ? "info"
              : order.orderStatus === "InProgress"
              ? "primary"
              : order.orderStatus === "Delivered"
              ? "dark"
              : "light"
          }
          variant="gradient"
          size="sm"
        />
      ),
      mosque: order?.mosque?.name,
      products: (
        <ul>
          {order.products.map((product) => (
            <li key={product.product._id}>
              {product.product?.name?.en || t("orders.unknownProduct")} {t("orders.quantity")}:{" "}
              {product.quantity}
            </li>
          ))}
        </ul>
      ),
      assignTo: (order.orderStatus === "Paid" || order.orderStatus === "InProgress") && (
        <div>
          {order.operator?.name ? (
            <div style={{ marginBottom: "5px", fontSize: "0.8rem", fontWeight: "500" }}>
              {t("orders.currentlyAssignedTo")}: {order.operator.name}
            </div>
          ) : null}
          <Select
            value={order.operator?._id || ""}
            onChange={(e) => handleAssignToOperator(order._id, e.target.value)}
            variant="outlined"
            size="small"
            sx={{
              minWidth: "120px",
              fontSize: "0.75rem",
            }}
            displayEmpty
          >
            <MenuItem value="" disabled>
              {order.operator ? t("orders.reassign") : t("orders.assignTo")}
            </MenuItem>
            {operators?.map((operator) => (
              <MenuItem key={operator._id} value={operator._id}>
                {operator.name}
              </MenuItem>
            ))}
          </Select>
        </div>
      ),
      actions: (
        <div className="flex justify-center space-x-2">
          <IconButton color="secondary" onClick={() => handleViewProducts(order.products)}>
            <VisibilityIcon />
          </IconButton>
          <IconButton color="primary" size="medium" onClick={() => handleEditClick(order)}>
            <EditIcon fontSize="medium" />
          </IconButton>
        </div>
      ),
    }));
  }, [loadingOrders, orders, operators]);

  return {
    columns: [
      { Header: t("orders.columns.orderId"), accessor: "orderId", align: "center" },
      { Header: t("orders.columns.userId"), accessor: "userId", align: "center" },
      { Header: t("orders.columns.customer"), accessor: "customer", align: "center" },
      { Header: t("orders.columns.totalPrice"), accessor: "totalPrice", align: "center" },
      { Header: t("orders.columns.orderDate"), accessor: "orderDate", align: "center" },
      { Header: t("orders.columns.paymentMethod"), accessor: "paymentMethod", align: "center" },
      { Header: t("orders.columns.orderStatus"), accessor: "orderStatus", align: "center" },
      { Header: t("orders.columns.mosque"), accessor: "mosque", align: "center" },
      { Header: t("orders.columns.products"), accessor: "products", align: "center" },
      { Header: t("orders.columns.assignTo"), accessor: "assignTo", align: "center" },
      { Header: t("orders.columns.actions"), accessor: "actions", align: "center" },
    ],
    rows,
    isModalOpen,
    handleCloseModal,
    fetchProducts: getSelectedProducts,
    handleCloseEditModal,
    isEditModalOpen,
    editedOrder,
    notification,
    setNotification,
    handleSaveChanges,
    setEditedOrder,
    orders,
    isPaymentVerificationModalOpen,
    handleClosePaymentVerificationModal,
    handleAcceptPayment,
    handleRejectPayment,
    selectedPaymentOrder,
  };
}
